import { AppButton, AppInput, AppTextarea } from 'components/form';
import { AppDialog, AppSectionErrorBoundary, AppSpacer } from 'components/ui';
import { useCallback, useEffect, useState } from 'react';

import { IUserDiagnostic, UserDiagnosticsState, UserDiagnosticsTag } from 'types/models/UserDiagnosticsModel';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import UserDiagnosticsCopyButton from '../UserDiagnosticsCopyButton';
import { useLocation } from 'react-router-dom';
import { useStores } from 'index';
import { Loader } from '@trackman/web-shared-components';
import { UserDiagnosticsMangeTab } from '../tabs/UserDiagnosticManageTab';
import AppUserDiagnosticsTagChip from 'components/ui/Chips/AppUserDiagnosticsTagChip';
import UserDiagnosticsAppDialogHeader from '../UserDiagnosticsAppDialogHeader';
import AppLoader from 'components/ui/AppLoader';
import UserDiagnosticsImagePreviewModal from '../modals/UserDiagnosticsManageDialog';
import { isMeasurementBodyParseable } from 'utilities/ParsingHelper';
import { RequestFtmfDialog } from './RequestFtmfDialog';

interface IUserDiagnosticsControlDialogProps {
    isOpened: boolean;
    onClose: () => void;
    userDiagnostic: IUserDiagnostic | null;
    isDownloadingUserDiagnosticsFile: boolean;
    isCreatingTicket: boolean;
}

export const UserDiagnosticsManageDialog = observer(({ isOpened, onClose, userDiagnostic: userDiagnostic, isDownloadingUserDiagnosticsFile, isCreatingTicket }: IUserDiagnosticsControlDialogProps) => {
    const location = useLocation();
    const { t } = useTranslation();
    const { userDiagnosticStore } = useStores();
    const [downloadingElement, setDownloadingElement] = useState<string | null>(null);
    const [isRequestFtmfOpen, setIsRequestFtmfOpen] = useState(false);

    const onCancelHandler = useCallback(() => {
        onClose();
    }, [onClose]);

    const onCloseReportHandler = useCallback(() => {
        if (userDiagnostic === null) {
            return;
        }

        userDiagnosticStore.toggleCloseUserDiagnosticState(userDiagnostic)
    }, [userDiagnostic]);

    useEffect(() => {
        if (!isDownloadingUserDiagnosticsFile && downloadingElement !== null) {
            setDownloadingElement(null);
        }
    }, [isDownloadingUserDiagnosticsFile]);

    const OnDownload = useCallback((key: string) => {
        if (userDiagnostic === null) {
            return;
        }
        setDownloadingElement(key)

        userDiagnosticStore.downloadUserDiagnosticsFile(userDiagnostic?.id ?? null)
    }, [userDiagnostic]);

    return (
        <AppDialog
            title={`${userDiagnostic?.userSubject || ''}`}
            isOpened={isOpened}
            onClose={onClose}
            maxWidth={'lg'}
            customHeader={<UserDiagnosticsAppDialogHeader title={`${userDiagnostic?.userSubject || ''}`} stickyHeader={true} userDiagnostic={userDiagnostic} />}
        >
            <div className='flex-auto d-flex flex-column justify-content-between'>
                <AppSectionErrorBoundary>
                    <div>
                        <section className='container'>
                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('UserDiagnostics.Section.UserDiagnostic')}</span>

                            </h3>
                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('UserDiagnostics.Column.Id')}
                                    name={'id'}
                                    value={userDiagnostic?.id ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer space='15px' />
                                <AppInput
                                    label={t('UserDiagnostics.Column.AddedAt')}
                                    name={'addedAt'}
                                    value={userDiagnostic?.addedAtUtc ?? '-'}
                                    disabled={true}
                                />
                            </div>
                            <AppSpacer space='15px' />

                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('UserDiagnostics.Section.Organization')}</span>
                            </h3>
                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('UserDiagnostics.Column.OrganizationId')}
                                    name={'organizationId'}
                                    value={userDiagnostic?.organizationId ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer isHorizontal space='1rem' />
                                <AppInput
                                    label={t('organizationName')}
                                    name={'organizationName'}
                                    value={userDiagnostic?.organizationName ?? '-'}
                                    disabled={true}
                                />
                            </div>
                            <AppSpacer space='15px' />

                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('UserDiagnostics.Section.Operator')}</span>
                            </h3>
                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('UserDiagnostics.Column.OperatorId')}
                                    name={'operatorId'}
                                    value={userDiagnostic?.operatorId ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer isHorizontal space='1rem' />
                                <AppInput
                                    label={t('UserDiagnostics.Column.OperatorEmail')}
                                    name={'email'}
                                    value={userDiagnostic?.operatorEmail ?? '-'}
                                    disabled={true}
                                />
                            </div>
                            <AppSpacer space='15px' />

                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('UserDiagnostics.Section.Info')}</span>
                            </h3>
                            <AppInput
                                label={t('UserDiagnostics.Column.Subject')}
                                name={'subject'}
                                value={userDiagnostic?.userSubject ?? '-'}
                                disabled={true}
                            />
                            <AppSpacer space='15px' />

                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('UserDiagnostics.Column.AppVersion')}
                                    name={'version'}
                                    value={userDiagnostic?.appVersion ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer isHorizontal space='1rem' />
                                <AppInput
                                    label={t('UserDiagnostics.Column.HasDiagnosticsPackage')}
                                    name={'hasDiagnosticsPackage'}
                                    value={`${userDiagnostic?.hasDiagnosticsPackage ?? false}`}
                                    disabled={true}
                                />
                            </div>
                            <AppSpacer space='15px' />

                            <AppTextarea
                                label={t('UserDiagnostics.Column.Description')}
                                name={'description'}
                                value={userDiagnostic?.userDescription ?? '-'}
                                disabled={true}
                                onChange={() => { }}
                            />
                            <AppSpacer space='15px' />
                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('UserDiagnostics.Section.Tags')}</span>
                            </h3>
                            <div className='d-flex flex-row gap-10px my-3'>
                                {userDiagnostic?.userDiagnosticsTags?.map((x, index) =>
                                    <AppUserDiagnosticsTagChip key={`tag-${index}`} userDiagnosticsTag={x as UserDiagnosticsTag} />)
                                }
                            </div>
                            <AppSpacer space='15px' />
                            <UserDiagnosticsMangeTab isCreatingTicket={isCreatingTicket} isLoading={false} isDownloadingUserDiagnosticsFile={isDownloadingUserDiagnosticsFile} userDiagnostic={userDiagnostic} />
                        </section>
                        <AppSpacer space='20px' />
                    </div>
                    <footer className='d-flex align-items-center justify-content-between p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
                        <AppButton variant='secondary' onClick={onCancelHandler} data-test='user-dialog-cancel-button'>
                            {t('cancel')}
                        </AppButton>
                        <UserDiagnosticsCopyButton localization={t('UserDiagnostics.Action.CopyUrl')} text={`${window.location.origin}${location.pathname}${location.search}`} />
                        <AppLoader isShowingLoader={isDownloadingUserDiagnosticsFile && downloadingElement === `entry-all`}>
                            <AppButton
                                variant='secondary'
                                className='border-secondary'
                                disabled={isDownloadingUserDiagnosticsFile || !userDiagnostic?.hasDiagnosticsPackage}
                                onClick={() => OnDownload(`entry-all`)}
                            >
                                {t('UserDiagnostics.Action.Download')}
                            </AppButton>
                        </AppLoader>

                        <AppButton
                            variant='secondary'
                            className='border-secondary'
                            disabled={!isMeasurementBodyParseable(userDiagnostic?.rawInfoContext)}
                            onClick={() => setIsRequestFtmfOpen(true)}
                        >
                            {t('TrackingDiagnostics.RequestFtmf.Button')}
                        </AppButton>

                        <AppLoader isShowingLoader={userDiagnosticStore.isUpdatingState}>
                            <AppButton
                                variant={userDiagnostic?.state === UserDiagnosticsState.Closed ? 'primary' : 'danger'}
                                onClick={() => onCloseReportHandler()}
                                disabled={userDiagnosticStore.isBusy}
                            >
                                {t(userDiagnostic?.state === UserDiagnosticsState.Closed ? 'UserDiagnostics.Action.ReopenState' : 'UserDiagnostics.Action.CloseState')}
                            </AppButton>
                        </AppLoader>
                    </footer>
                </AppSectionErrorBoundary>
            </div>
            <UserDiagnosticsImagePreviewModal userDiagnosticsId={userDiagnostic?.id || null} />
            <RequestFtmfDialog
                isOpened={isRequestFtmfOpen}
                onClose={() => setIsRequestFtmfOpen(false)}
                userDiagnostic={userDiagnostic}
            />
        </AppDialog>
    );
});